import { Link } from "react-router-dom";
import { trackNavClick, trackExternalLink, trackCaseStudyView, trackPhoneCall } from "@/lib/analytics";
import { Instagram, Facebook, Linkedin, Twitter, Phone } from "lucide-react";
import logo from "@/assets/zoom-logo-blue.png?format=webp";
import { caseStudies } from "@/data/caseStudies";

export function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="relative isolate overflow-hidden border-t border-foreground bg-background">
      <div className="w-full max-w-[1400px] mx-auto px-6 lg:px-12 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10 md:gap-8">
          {/* Brand */}
          <div>
            <Link to="/">
              <img
                src={logo}
                alt="The Zoom Agency"
                className="h-7 w-auto"
              />
            </Link>
            <p className="mt-4 text-sm text-muted-foreground max-w-xs">
              A creative and performance consultancy, working with senior teams.
            </p>
            <a
              href="tel:+442034883090"
              onClick={() => trackPhoneCall("footer")}
              className="inline-flex items-center gap-2 mt-4 text-sm font-medium text-foreground hover:text-primary transition-colors"
              aria-label="Call us on +44 (0) 203 488 3090"
            >
              <Phone size={16} />
              +44 (0) 203 488 3090
            </a>
            <div className="flex items-center gap-4 mt-6">
              <a
                href="https://www.instagram.com/thezoomagency"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackExternalLink('https://www.instagram.com/thezoomagency')}
                className="text-muted-foreground hover:text-primary hover:scale-110 transition-all duration-200"
                aria-label="Follow us on Instagram"
              >
                <Instagram size={20} />
              </a>
              <a
                href="https://www.facebook.com/TheZoomAgency"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackExternalLink('https://www.facebook.com/TheZoomAgency')}
                className="text-muted-foreground hover:text-primary hover:scale-110 transition-all duration-200"
                aria-label="Follow us on Facebook"
              >
                <Facebook size={20} />
              </a>
              <a
                href="https://www.linkedin.com/company/thezoomagency"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackExternalLink('https://www.linkedin.com/company/thezoomagency')}
                className="text-muted-foreground hover:text-primary hover:scale-110 transition-all duration-200"
                aria-label="Follow us on LinkedIn"
              >
                <Linkedin size={20} />
              </a>
              <a
                href="https://x.com/thezoomagency"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackExternalLink('https://x.com/thezoomagency')}
                className="text-muted-foreground hover:text-primary hover:scale-110 transition-all duration-200"
                aria-label="Follow us on X"
              >
                <Twitter size={20} />
              </a>
            </div>
          </div>

          {/* Navigate */}
          <div>
            <h4 className="text-sm font-medium text-foreground mb-4">Navigate</h4>
            <ul className="space-y-2">
              {[
                { label: "About", to: "/about" },
                { label: "Services", to: "/services" },
                { label: "Work", to: "/work" },
                { label: "Clients", to: "/who-we-work-with" },
                { label: "Insights", to: "/insights" },
                { label: "Marketing Pulse", to: "/insights/pulse" },
                { label: "Superintelligence Advantage", to: "/superintelligence-advantage" },
                { label: "Showreel", to: "/showreel" },
                { label: "FAQ", to: "/faq" },
                { label: "Careers", to: "/careers" },
              ].map((link) => (
                <li key={link.to}>
                  <Link to={link.to} onClick={() => trackNavClick(link.label)} className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-sm font-medium text-foreground mb-4">Services</h4>
            <ul className="space-y-2">
              {[
                { label: "Brand Strategy", to: "/services/brand-strategy" },
                { label: "Content Creation", to: "/services/content-creation" },
                { label: "Graphic Design", to: "/services/graphic-design" },
                { label: "Paid Media", to: "/services/paid-media" },
                { label: "Social Media", to: "/services/social-media-marketing" },
                { label: "Web Development", to: "/services/web-development" },
                { label: "Superintelligence Readiness", to: "/services/superintelligence-readiness" },
              ].map((service) => (
                <li key={service.to}>
                  <Link to={service.to} onClick={() => trackNavClick(service.label)} className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                    {service.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Work */}
          <div>
            <h4 className="text-sm font-medium text-foreground mb-4">Agency Work</h4>
            <ul className="space-y-2">
              {caseStudies.slice(0, 5).map((study) => (
                <li key={study.slug}>
                  <Link
                    to={`/work/${study.slug}`}
                    onClick={() => trackCaseStudyView(study.title)}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {study.title.split(' ').slice(0, 2).join(' ')}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/work" onClick={() => trackNavClick('View All Work')} className="text-sm text-primary hover:text-primary/80 transition-colors font-medium">
                  View all →
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-border/30 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <p className="text-xs text-muted-foreground">
            © {currentYear} The Zoom Agency. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <Link to="/privacy" className="text-xs text-muted-foreground hover:text-foreground transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms" className="text-xs text-muted-foreground hover:text-foreground transition-colors">
              Terms of Use
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
